import { ChevronDown, ChevronRight, CircleAlert, Copy, EllipsisIcon, Info, Plus, SquareCheckIcon, SquareIcon } from 'lucide-react'
import { parseAsString, useQueryState } from 'nuqs'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { toast } from 'sonner'
import useSWR from 'swr'

import DropdownMenuIconItem from '@/components/DropdownMenuIconItem'
import { Money, PageHeader, SegmentedTabs } from '@/components/ds'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useSetPageHeader } from '@/contexts/PageHeaderContext'
import { apiFetcher } from '@/data/fetcher'
import { useRoleCheck } from '@/hooks/useRoleCheck'
import API from '@/lib/api'
import { formatDateTime } from '@/lib/formats/formatDateTime'
import { operations } from '@invo.space/shared'
import { Role } from '@/types/User'

import { BankTransactions } from './components/BankTransactions'
import { ServiceMark } from '@/components/ds'

import { bankProviders, providerForBankCode } from './components/bankUtils'

type GetBanksResponse = operations['getBanks']['responses']['200']['content']['application/json']

export default function BankPage() {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'bank',
  })
  const [account, setAccount] = useQueryState('account', parseAsString)
  const [balanceDialogOpen, setBalanceDialogOpen] = useState(false)
  const [balanceInput, setBalanceInput] = useState('')
  const [isSavingBalance, setIsSavingBalance] = useState(false)
  const canManage = useRoleCheck([Role.owner, Role.manager])

  const { data, isLoading, mutate: reloadBankAccounts } = useSWR<GetBanksResponse>('/api/v2/bank', apiFetcher)

  const accounts = data?.data.accounts ?? []
  const balances = Object.values(data?.data.balances ?? {})
  const hasAccounts = accounts.length > 0
  const selectedAccount = account && accounts.some((a) => a.id === account) ? account : accounts[0]?.id
  const currentAccount = selectedAccount ? accounts.find((acc) => acc.id === selectedAccount) : undefined
  const provider = providerForBankCode(currentAccount?.bankAccount.bankCode)

  const toggleFetchEnabled = useCallback(() => {
    if (currentAccount) {
      API.post(`/api/v2/bank/${currentAccount.id}`, {
        fetchEnabled: currentAccount.fetchEnabled === 'enabled' ? 'disabled' : 'enabled',
      }).then(() => {
        reloadBankAccounts()
      })
    }
  }, [currentAccount, reloadBankAccounts])

  const openBalanceDialog = useCallback(() => {
    if (currentAccount) {
      setBalanceInput((Number(currentAccount.balance.amount) / 100).toString())
      setBalanceDialogOpen(true)
    }
  }, [currentAccount])

  const saveBalance = useCallback(() => {
    if (!currentAccount) return

    const balance = Number(balanceInput)
    if (Number.isNaN(balance)) return

    setIsSavingBalance(true)
    API.post(`/api/v2/bank/${currentAccount.id}`, { balance })
      .then(() => {
        setBalanceDialogOpen(false)
        reloadBankAccounts()
      })
      .finally(() => {
        setIsSavingBalance(false)
      })
  }, [balanceInput, currentAccount, reloadBankAccounts])

  const balanceTooltip = currentAccount?.balanceSetAt
    ? `${t('balanceTooltip')} ${t('balanceTooltipSetAt', {
        date: formatDateTime(currentAccount.balanceSetAt),
      })}`
    : t('balanceTooltip')

  const addAccountButton = canManage ? (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline">
          <Plus />
          {t('addAccount')}
          <ChevronDown className="text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {bankProviders.map((p) => (
          <DropdownMenuItem key={p.slug} asChild>
            <Link to={`/bank/${p.slug}`} className="cursor-pointer">
              <ServiceMark service={p.slug} size="sm" />
              {t(`${p.slug}.title`)}
            </Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  ) : null

  useSetPageHeader({
    breadcrumbs: [
      {
        href: '/bank',
        children: t('title'),
      },
    ],
    newButton: 'invoice',
  })

  const accountNumber = currentAccount ? `${currentAccount.bankAccount.accountNumber}/${currentAccount.bankAccount.bankCode}` : ''

  const copy = (value: string, label: string) => {
    navigator.clipboard.writeText(value).then(() => toast.success(`${label} zkopírováno`))
  }

  return (
    <>
      <title>{t('title')}</title>

      <div className="mx-auto w-full max-w-[1280px]">
        <PageHeader
          title={t('title')}
          description={
            hasAccounts ? (
              <span className="inline-flex flex-wrap items-baseline gap-x-1.5">
                <span>
                  {accounts.length} {accounts.length === 1 ? 'účet' : accounts.length <= 4 ? 'účty' : 'účtů'}
                  {balances.length > 0 && ', celkový zůstatek'}
                </span>
                {balances.map((b, i) => (
                  <span key={b.currency}>
                    <Money amount={b.amount} currency={b.currency} size="sm" className="text-foreground" />
                    {i < balances.length - 1 && ','}
                  </span>
                ))}
              </span>
            ) : (
              'Bankovní účty a jejich pohyby'
            )
          }
          actions={hasAccounts ? addAccountButton : null}
        />

        {isLoading && (
          <div className="space-y-6">
            <Skeleton className="h-28 w-full rounded-lg" />
            <Skeleton className="h-24 w-full rounded-lg" />
            <Skeleton className="h-64 w-full rounded-lg" />
          </div>
        )}

        {!isLoading && !hasAccounts && <ConnectBank canManage={canManage} title={t('emptyState.title')} />}

        {hasAccounts && currentAccount && (
          <>
            {accounts.length > 1 && (
              <SegmentedTabs
                className="mb-4"
                value={currentAccount.id}
                onChange={(value) => value !== selectedAccount && setAccount(value)}
                options={accounts.map((acc) => ({ value: acc.id, label: acc.name }))}
              />
            )}

            <section className="mb-6 flex flex-wrap items-start justify-between gap-6 rounded-lg border px-5 py-4">
              <div className="min-w-0 space-y-2">
                <p className="text-xs text-muted-foreground">{t('balance')}</p>
                <div className="flex items-center gap-1.5">
                  <Money amount={currentAccount.balance.amount} currency={currentAccount.balance.currency} size="xl" />
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button type="button" className="text-muted-foreground hover:text-foreground" aria-label="O zůstatku">
                        <Info className="size-4" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">{balanceTooltip}</TooltipContent>
                  </Tooltip>
                </div>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                  <span className="font-medium">{currentAccount.name}</span>
                  <button
                    type="button"
                    onClick={() => copy(accountNumber, 'Číslo účtu')}
                    className="inline-flex items-center gap-1 text-muted-foreground tabular hover:text-foreground"
                  >
                    {accountNumber}
                    <Copy className="size-3.5" />
                  </button>
                  {currentAccount.bankAccount.iban && (
                    <button
                      type="button"
                      onClick={() => copy(currentAccount.bankAccount.iban!, 'IBAN')}
                      className="inline-flex items-center gap-1 text-muted-foreground tabular hover:text-foreground"
                    >
                      {currentAccount.bankAccount.iban}
                      <Copy className="size-3.5" />
                    </button>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="text-right text-xs text-muted-foreground">
                  <p className="inline-flex items-center gap-1.5">
                    <span
                      className={currentAccount.fetchEnabled === 'enabled' ? 'size-1.5 rounded-full bg-success' : 'size-1.5 rounded-full bg-warning'}
                      aria-hidden
                    />
                    {currentAccount.fetchEnabled === 'enabled' ? 'Stahování zapnuto' : 'Stahování vypnuto'}
                  </p>
                  {currentAccount.lastFetch && <p className="tabular">Naposledy {formatDateTime(currentAccount.lastFetch)}</p>}
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="icon" aria-label="Akce účtu">
                      <EllipsisIcon />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={openBalanceDialog}>{t('setBalance')}</DropdownMenuItem>
                    <DropdownMenuItem onClick={toggleFetchEnabled}>
                      <DropdownMenuIconItem
                        icon={currentAccount.fetchEnabled === 'enabled' ? <SquareCheckIcon /> : <SquareIcon />}
                        text={t(`state.${currentAccount.fetchEnabled}`)}
                      />
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </section>

            {currentAccount.fetchEnabled === 'disabled' && (
              <Alert className="mb-6 border-transparent bg-warning-bg text-warning">
                <CircleAlert />
                <AlertTitle>
                  {currentAccount.fetchDisabledReason
                    ? t(`fetchDisabledReason.${currentAccount.fetchDisabledReason}`)
                    : t('fetchDisabledWarning')}
                </AlertTitle>
                <AlertDescription>
                  {currentAccount.fetchDisabledReason === 'token_expired'
                    ? 'Nové transakce se nestahují. Vytvořte v bance nový token a propojte účet znovu.'
                    : 'Nové transakce se nestahují. Stahování zapnete v menu účtu.'}
                  {currentAccount.fetchDisabledReason && provider && canManage && (
                    <Link to={`/bank/${provider.slug}`} className="mt-1 inline-flex items-center gap-0.5 font-medium text-link hover:underline">
                      Obnovit propojení s bankou
                      <ChevronRight className="size-3.5" />
                    </Link>
                  )}
                </AlertDescription>
              </Alert>
            )}

            <BankTransactions key={currentAccount.id} account={currentAccount} />
          </>
        )}
      </div>

      <Dialog open={balanceDialogOpen} onOpenChange={setBalanceDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('setBalanceDialog.title')}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-2">
            <Label htmlFor="balance">{t('setBalanceDialog.label')}</Label>
            <Input
              id="balance"
              type="number"
              step="0.01"
              value={balanceInput}
              onChange={(event) => setBalanceInput(event.target.value)}
              className="tabular"
            />
            <p className="text-xs text-muted-foreground">{t('balanceTooltip')}</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBalanceDialogOpen(false)}>
              {t('setBalanceDialog.cancel')}
            </Button>
            <Button onClick={saveBalance} disabled={isSavingBalance}>
              {t('setBalanceDialog.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

/** Prázdný stav: rovnou nabídne banky, které umíme propojit. */
const ConnectBank = ({ canManage, title }: { canManage: boolean; title: string }) => (
  <div className="grid gap-6 lg:grid-cols-[1fr_1.4fr]">
    <div className="space-y-2 pt-1">
      <h2 className="text-base font-medium">{title}</h2>
      <p className="max-w-sm text-sm text-muted-foreground text-pretty">
        Propojte účet a pohyby se budou stahovat automaticky. Příchozí platby spárujeme s fakturami podle variabilního symbolu a faktury
        označíme jako zaplacené.
      </p>
      {!canManage && <p className="text-sm text-muted-foreground">Účet může propojit vlastník nebo správce.</p>}
    </div>
    <ul className="divide-y overflow-hidden rounded-lg border">
      {bankProviders.map((p) => {
        const inner = (
          <>
            <div className="flex min-w-0 items-center gap-3">
              <ServiceMark service={p.slug} />
              <div className="min-w-0">
                <p className="text-sm font-medium">{p.name}</p>
                <p className="text-xs text-muted-foreground">{p.method}</p>
              </div>
            </div>
            {canManage && <ChevronRight className="size-4 shrink-0 text-muted-foreground" />}
          </>
        )
        return (
          <li key={p.slug}>
            {canManage ? (
              <Link to={`/bank/${p.slug}`} className="flex items-center justify-between gap-4 px-4 py-3 transition-colors hover:bg-muted">
                {inner}
              </Link>
            ) : (
              <div className="flex items-center justify-between gap-4 px-4 py-3">{inner}</div>
            )}
          </li>
        )
      })}
    </ul>
  </div>
)
